import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as css from './checkableInput.module.scss';
import Icon, { IconType } from '../icon';
import classNames from 'classnames';
import { isNullOrUndefined } from '../../util/isNullOrUndefined';

export type CustomCheckedState = {
  checked: boolean;
  indeterminate: boolean;
};

interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  id: string;
  label?: string;
  wrapperClassname?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  indeterminate?: boolean;
  setChecked: (checked: boolean) => void;
}

const Checkbox = ({
  id,
  label,
  checked,
  setChecked,
  disabled,
  tabIndex,
  wrapperClassname,
  inputRef,
  indeterminate,
  ...props
}: CheckboxProps) => {
  const inputId = `checkbox-${id}`;

  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handleChange = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (!disabled) setChecked(!checked);
  };

  const handleKeyEvent = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') handleChange(event);
  };

  const innerRef = useRef<HTMLInputElement>(null);
  const ref = inputRef || innerRef;

  useEffect(() => {
    const { current = undefined } = ref;

    if (current && !isNullOrUndefined(indeterminate)) {
      current.indeterminate = indeterminate;
    }
  }, [ref, indeterminate]);

  const iconType = useMemo(() => {
    if (indeterminate) {
      return IconType.IndeterminateCheckBox;
    }

    return checked ? IconType.CheckBox : IconType.CheckBoxOutline;
  }, [checked, indeterminate]);

  return (
    <div
      className={classNames(
        css.checkableInputWrapper,
        wrapperClassname,
        disabled && css.disabled,
      )}
      onClick={handleChange}
    >
      <Icon
        className={classNames(
          css.icon,
          checked ? css.checked : css.unchecked,
          isFocused && css.isFocused,
          disabled && css.disabled,
        )}
        type={iconType}
      />
      <input
        id={inputId}
        type="checkbox"
        checked={checked}
        onChange={() => {
          /* Method is not to be used, but must be defined. Key events should be put directly on input and mouse events on wrapper */
        }}
        onKeyDown={handleKeyEvent}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={disabled}
        ref={ref}
        aria-checked={indeterminate ? 'mixed' : checked}
        {...props}
      />
      <label
        className={classNames(
          disabled && css.disabled,
          !label && css.emptyLabel,
        )}
        htmlFor={inputId}
        tabIndex={disabled ? undefined : tabIndex}
      >
        {label}
      </label>
    </div>
  );
};

export default Checkbox;
